import styled from "@emotion/styled";
import React from "react";

/** Loosely-typed node from Payload CMS rich text JSON */
interface RichTextNode {
  type?: string;
  text?: string;
  url?: string;
  newTab?: boolean;
  textAlign?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  color?: string;
  value?: { url?: string; filename?: string; width?: number; height?: number };
  children?: RichTextNode[];
}

const StyledParagraph = styled.div`
  label: StyledParagraph;
  margin: 0.5rem;
  line-height: 1.6;
`;

const StyledHeading = styled.h1`
  label: StyledHeading;
  margin: 2rem 0 1rem;
  font-weight: bold;
`;

const StyledList = styled.ul`
  label: StyledList;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
`;

const StyledAlignedDiv = styled.div<{ align?: string }>`
  label: StyledAlignedDiv;
  text-align: ${(props) => props.align || "left"};
`;

function parseRichText(value: string | null | undefined): unknown {
  return JSON.parse(value?.replaceAll("\n", "") || "{}");
}

export function isEmpty(value: string | null | undefined): boolean {
  if (!value) return true;
  try {
    const isEmptyNode = (node: RichTextNode): boolean => {
      if (node.text !== undefined) {
        return !node.text || node.text.trim() === "";
      }
      return node.children?.every(isEmptyNode) ?? true;
    };

    const parsed = parseRichText(value);
    if (Array.isArray(parsed)) {
      return parsed.every((block: RichTextNode) => isEmptyNode(block));
    }
    return isEmptyNode(parsed as RichTextNode);
  } catch (e) {
    console.error(e);
    return false;
  }
}

function renderChildren(
  children: RichTextNode[],
  prefix: string,
): React.ReactNode[] {
  return children.map((child, i) => renderLeaf(child, i, `${prefix}-${i}`));
}

function renderLeaf(
  leaf: RichTextNode,
  index: number,
  keyPrefix: string,
): React.ReactNode {
  if (leaf.type) {
    return renderElement(leaf, index);
  }

  if (leaf.children) {
    return leaf.children
      .map((child, i) => renderLeaf(child, i, `${keyPrefix}-${i}`))
      .join("");
  }

  let text: React.ReactNode = leaf.text ?? "";

  if (leaf.bold) {
    text = <strong key={`${keyPrefix}-bold`}>{text}</strong>;
  }
  if (leaf.italic) {
    text = <em key={`${keyPrefix}-italic`}>{text}</em>;
  }
  if (leaf.underline) {
    text = <u key={`${keyPrefix}-underline`}>{text}</u>;
  }
  if (leaf.code) {
    text = <code key={`${keyPrefix}-code`}>{text}</code>;
  }

  if (leaf.color) {
    text = (
      <span key={`${keyPrefix}-color`} style={{ color: leaf.color }}>
        {text}
      </span>
    );
  }

  return text;
}

function renderElement(element: RichTextNode, index: number): React.ReactNode {
  const withAlignment = (content: React.ReactNode) => {
    if (!element.textAlign) return content;
    return (
      <StyledAlignedDiv align={element.textAlign}>{content}</StyledAlignedDiv>
    );
  };

  const children = element.children ?? [];

  switch (element.type) {
    case "upload":
      return withAlignment(
        <img
          key={index}
          src={element.value?.url}
          alt={element.value?.filename}
          width={element.value?.width}
          height={element.value?.height}
        />,
      );

    case "paragraph":
      return withAlignment(
        <StyledParagraph key={index}>
          {renderChildren(children, `p-${index}`)}
        </StyledParagraph>,
      );

    case "heading-one":
      return withAlignment(
        <StyledHeading as="h1" key={index}>
          {renderChildren(children, `h1-${index}`)}
        </StyledHeading>,
      );

    case "heading-two":
      return withAlignment(
        <StyledHeading as="h2" key={index}>
          {renderChildren(children, `h2-${index}`)}
        </StyledHeading>,
      );

    case "bulleted-list":
    case "ul":
      return (
        <StyledList key={index}>
          {children.map((child, i) => renderElement(child, i))}
        </StyledList>
      );

    case "list-item":
    case "li":
      return (
        <li key={index}>
          {children.map((child, i) => {
            // Unwrap nested list-items to avoid invalid <li> inside <li>
            if (child.type === "li" || child.type === "list-item") {
              return (child.children ?? []).map((grandchild, j) =>
                renderLeaf(grandchild, j, `li-${index}-${i}-${j}`),
              ) || [];
            }
            return renderLeaf(child, i, `li-${index}-${i}`);
          })}
        </li>
      );

    case "link":
      return withAlignment(
        <a
          key={index}
          href={element.url}
          target={element.newTab ? "_blank" : undefined}
          rel={element.newTab ? "noopener noreferrer" : undefined}
        >
          {renderChildren(children, `link-${index}`)}
        </a>,
      );

    default:
      return withAlignment(
        <StyledParagraph key={index}>
          {renderChildren(children, `default-${index}`)}
        </StyledParagraph>,
      );
  }
}

export function RichText({
  value,
}: {
  value?: string | null;
}): React.ReactNode {
  if (!value || value === "" || isEmpty(value)) return null;

  try {
    const parsedValue = parseRichText(value) as RichTextNode[];
    const content = parsedValue.map((element, i) => renderElement(element, i));
    return <>{content}</>;
  } catch (e) {
    console.error(e);
    return <>{value}</>;
  }
}

export function toPlainText(value: string | null | undefined): string {
  try {
    const extractText = (node: RichTextNode): string => {
      if (node.text !== undefined) {
        return node.text;
      }
      return node.children?.map(extractText).join("") ?? "";
    };

    const parsed = parseRichText(value);
    if (Array.isArray(parsed)) {
      return parsed.map(extractText).join("");
    }
    return extractText(parsed as RichTextNode);
  } catch (e) {
    console.error(e);
    return "";
  }
}
